/**
 * @file        src/pages/hotel/CancellationList.jsx
 * @description Hotel Cancellation List — cancelled / cancellation-requested /
 *              refunded bookings (`/hotel/cancellations`). Legacy parity columns:
 *              C ID / B ID / Request Date / Hotel / City / Check In / Check Out /
 *              Customer / Status / Action (Details).
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Legacy parity columns (request date, hotel, city, stay,
 *                          customer) + a Details modal. There is no separate
 *                          cancellations table, so "C ID" is the booking row's id
 *                          and "B ID" its supplier booking reference.
 *   - 2026-08-10 Claude — Added title/subtitle/audience props (defaulting to the
 *                          unchanged B2C behaviour) so CorporateCancellationList.jsx
 *                          can reuse this page fixed to audience=b2b instead of
 *                          forking it — the backend now actually applies `audience`
 *                          (it previously accepted but ignored the param).
 *   - 2026-08-10 Claude — Details now opens the full BookingDetailsDrawer (the
 *                          cart view: stay, refund pipeline, timeline, and the
 *                          status-gated Invoice action) instead of the thin
 *                          row-fed BookingDetailsModal, which is deleted — this
 *                          was its only caller, and it could not offer the
 *                          Invoice a cancelled booking is entitled to.
 */
import { useState } from 'react';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import { day, dateTime, statusTone, customerCell } from './cells';
import BookingDetailsDrawer from './BookingDetailsDrawer';

export default function CancellationList({
  title = 'Hotel Cancellation List',
  subtitle = 'B2C Hotel · cancellations & refunds',
  audience = 'b2c',
} = {}) {
  const [detailsId, setDetailsId] = useState(null);

  const rowActions = (row) => (
    <div className="row-actions">
      <button className="btn xs ghost" onClick={() => setDetailsId(row.id)} title="Details">
        <Icon name="search" /> Details
      </button>
    </div>
  );

  return (
    <>
      <ReadOnlyListPage
        title={title}
        subtitle={subtitle}
        endpoint="/hotel/cancellations"
        queryRoot={`hotel-cancellations-${audience}`}
        extraParams={{ audience }}
        searchLabel="Booking Ref / Hotel"
        actionsLabel="Action"
        rowActions={rowActions}
        showIndex={false}
        columns={[
          { key: 'id', label: 'C ID', render: (v) => <span className="mono">#{v}</span> },
          { key: 'booking_reference', label: 'B ID', render: (v) => <b className="mono">{v || '—'}</b> },
          { key: 'cancel_request_time', label: 'Request Date', render: dateTime },
          { key: 'hotel_name', label: 'Hotel', render: (v) => v || <span className="muted">—</span> },
          { key: 'city_name', label: 'City', render: (v) => v || <span className="muted">—</span> },
          { key: 'check_in', label: 'Check In', render: day },
          { key: 'check_out', label: 'Check Out', render: day },
          { key: 'customer_name', label: 'Customer', render: customerCell },
          { key: 'booking_status', label: 'Status', render: (v) => (v ? <Chip tone={statusTone(v)}>{v}</Chip> : '—') },
        ]}
      />
      {detailsId ? <BookingDetailsDrawer bookingId={detailsId} onClose={() => setDetailsId(null)} /> : null}
    </>
  );
}
